/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DeviceModel } from "../device";
import { Log } from "../logging";
import {
  CLAIM_JWT_TTL_SECONDS,
  JWT_CLOCK_SKEW_BACKDATE_SECONDS
} from "../../config";
import {
  CryptoProviderFactory,
  CryptoPrivateKey,
  JwtClaims
} from "../device/crypto";

const TAG: string = "claimJwtBuilder";

const cryptoProvider = CryptoProviderFactory.getProvider();

/** Primary JWT lifetime in seconds (1 hour). */
const PRIMARY_JWT_TTL_SECONDS: number = 3600;

/**
 * Exports the device public key as a PEM-encoded string and then
 * base64-encodes the entire PEM for inclusion in the claim JWT `pub` claim.
 */
async function getPublicKeyBase64Pem(): Promise<string> {
  const cryptoProvider = CryptoProviderFactory.getProvider();

  const publicKey = DeviceModel.getDlidPublicKey();

  if (publicKey === null) {
    throw new Error("Device public key is not initialised");
  }

  const pem: string = await cryptoProvider.getPublicKeyPem(publicKey);

  return btoa(pem);
}

/**
 * Imports the device RSA private key (JWK) as a CryptoKey for JWT signing.
 */
function getDevicePrivateKey(): CryptoPrivateKey {
  const privateKey = DeviceModel.getDlidPrivateKey();

  if (privateKey === null) {
    throw new Error("Device private key is not initialised");
  }

  return privateKey;
}

/**
 * Generates a UUID v4 string for use as a JWT `jti` claim.
 */
function generateJti(): string {
  return cryptoProvider.generateUuidV4();
}

/**
 * Builds and signs the JWT for the /claim API call.
 *
 * The claim JWT registers the device's public key with the domain-tokens
 * service. It is self-signed with the device's own RSA private key — the
 * key pair generated on first boot — so no build-time signing material is
 * involved.
 *
 * JWT structure (per the Unity end-to-end API sequence reference):
 * - Header: { alg: "RS512", kid: <client kid> }
 * - Payload: { dlid, kid, pub, nbf, exp }
 *
 * `nbf` is backdated by JWT_CLOCK_SKEW_BACKDATE_SECONDS so a device clock
 * running ahead of domain-tokens does not make the token "not valid yet";
 * `exp` stays anchored to the true now, keeping the TTL unchanged.
 *
 * @param selectedProfileId - Active profile ID included as the `spid` claim.
 * @returns The signed claim JWT string.
 */
export async function buildClaimJwt(
  selectedProfileId?: string
): Promise<string> {
  // Ensure DLID + RSA key pair are initialised before accessing keys
  await DeviceModel.initDlid();

  const dlid: string = DeviceModel.getDeviceLoggingId();
  const kid: string = DeviceModel.getPid();

  const pub: string = await getPublicKeyBase64Pem();

  Log.d(TAG, "buildClaimJwt() dlid=" + dlid + ", kid=" + kid);

  const privateKey: CryptoPrivateKey = getDevicePrivateKey();
  const now: number = Math.floor(Date.now() / 1000);
  const claims: JwtClaims = {
    dlid,
    kid,
    pub,
    nbf: now - JWT_CLOCK_SKEW_BACKDATE_SECONDS,
    exp: now + CLAIM_JWT_TTL_SECONDS
  };
  if (selectedProfileId !== undefined && selectedProfileId.length > 0) {
    claims.spid = selectedProfileId;
  }
  const jwt: string = await cryptoProvider.signJwtRS512(claims, privateKey, {
    alg: CryptoProviderFactory.RS512_ALGORITHM,
    kid
  });

  Log.d(TAG, "buildClaimJwt() JWT signed successfully");
  return jwt;
}

/**
 * Builds and signs the primary credential JWT used for /domainToken and /bind.
 *
 * This JWT is signed with the device's RSA-2048 private key (generated on
 * first boot and stored in IndexedDB). It proves the device possesses the
 * private key corresponding to the public key registered via /claim.
 *
 * JWT structure (per the Unity end-to-end API sequence reference):
 * - Header: { alg: "RS256", kid: pid }
 * - Payload: { dlid, iat, nbf, exp (now+1h), jti }
 *
 * `iat`/`nbf` are backdated by JWT_CLOCK_SKEW_BACKDATE_SECONDS so a device
 * clock running ahead of the validating service does not make the token "not
 * valid yet" (nor issued in the future); `exp` stays anchored to the true now,
 * keeping the TTL unchanged.
 *
 * @param selectedProfileId - Active profile ID included as the `spid` claim.
 * @returns The signed primary credential JWT string.
 */
export async function buildPrimaryCredentialJwt(
  selectedProfileId?: string
): Promise<string> {
  // Ensure DLID + RSA key pair are initialised before accessing keys
  await DeviceModel.initDlid();

  const dlid: string = DeviceModel.getDeviceLoggingId();
  const pid: string = DeviceModel.getPid();

  Log.d(TAG, "buildPrimaryCredentialJwt() dlid=" + dlid + ", pid=" + pid);

  const privateKey: CryptoPrivateKey = getDevicePrivateKey();
  const now: number = Math.floor(Date.now() / 1000);
  const jti: string = generateJti();
  const claims: JwtClaims = {
    dlid,
    iat: now - JWT_CLOCK_SKEW_BACKDATE_SECONDS,
    nbf: now - JWT_CLOCK_SKEW_BACKDATE_SECONDS,
    exp: now + PRIMARY_JWT_TTL_SECONDS,
    jti
  };
  if (selectedProfileId !== undefined && selectedProfileId.length > 0) {
    claims.spid = selectedProfileId;
  }

  const jwt = await cryptoProvider.signJwtRS256(claims, privateKey, {
    alg: CryptoProviderFactory.RS256_ALGORITHM,
    kid: pid
  });

  Log.d(TAG, "buildPrimaryCredentialJwt() JWT signed successfully");
  return jwt;
}
